/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import NoSSR from 'react-no-ssr';
import classNames from 'classnames';

import { trans } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import useHorizontalDrag from '@spotahome/ui-library/src/hooks/useHorizontalDrag';

import { useListingNavigation } from '../../context/ListingNavigationContext';

import NavigationAngle from './components/NavigationAngle';

import { getItems } from './utils/getItems';

import './ListingContentNavigation.scss';

const getSelectedItemClassNames = (index, selected) =>
  classNames('listing-content-navigation__list-item', {
    'listing-content-navigation__list-item--active': index === selected
  });

export const ListingContentNavigation = ({
  flags = {
    hasReviews: false,
    hasSurroundings: false
  },
  className = null
}) => {
  const [selectedItem, setSelectedItem] = useState(0);
  const {
    containerRef,
    isDragging,
    handleMouseDown,
    handleMouseUp,
    handleTouchStart,
    handleTouchEnd
  } = useHorizontalDrag({
    onMoveStart: () => {},
    onMove: () => {},
    itemsOnLeftClassName: 'listing-content-navigation__list--items-on-left',
    itemsOnRightClassName: 'listing-content-navigation__list--items-on-right'
  });

  const wrapperRef = useRef();
  const { sectionInView, scrollToSection, setHeight, navigationRef } =
    useListingNavigation();
  const items = getItems(flags, false);

  const handleNavClick = side => () => {
    const offset = side === 'left' ? -1 : 1;

    const current = items[selectedItem];
    const prevItem = items[selectedItem + offset];
    const { section } = prevItem ? prevItem.data : current.data;

    SoyuzAnalytics.sendGA4Event('listing-section-arrow-selected', {
      section
    });

    scrollToSection(section);
  };

  const handleItemClick = section => e => {
    e.preventDefault();
    e.stopPropagation();
    if (isDragging()) {
      return;
    }

    SoyuzAnalytics.sendGA4Event('listing-section-selected', {
      section
    });
    scrollToSection(section);
  };

  const resizeHandler = () => {
    if (navigationRef.current) {
      const { height: navHeight } =
        navigationRef.current.getBoundingClientRect();
      setHeight(navHeight);
    }
  };

  useEffect(() => {
    if (selectedItem !== sectionInView) {
      const next = items.findIndex(item => item.data.section === sectionInView);

      if (next >= 0) {
        setSelectedItem(next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionInView]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandler);
    resizeHandler();
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  const wrapperClassName = classNames('listing-content-navigation', className);

  return (
    <div className={wrapperClassName} ref={navigationRef}>
      <NoSSR />
      <div className="listing-content-navigation__wrapper">
        <div
          ref={wrapperRef}
          className="listing-content-navigation__inner-wrapper"
        >
          <ul
            ref={containerRef}
            className="listing-content-navigation__list"
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {items.map((item, index) => {
              const { section, textKey } = item.data;
              return (
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                <li
                  ref={item.ref}
                  key={section}
                  className={getSelectedItemClassNames(index, selectedItem)}
                  onClick={handleItemClick(section)}
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                  tabIndex="0"
                >
                  {trans(textKey)}
                </li>
              );
            })}
          </ul>
          <NavigationAngle
            side="left"
            onClick={handleNavClick('left')}
            className="listing-content-navigation__arrow listing-content-navigation__arrow--left"
          />

          <NavigationAngle
            side="right"
            onClick={handleNavClick('right')}
            className="listing-content-navigation__arrow listing-content-navigation__arrow--right"
          />
        </div>
      </div>
    </div>
  );
};

ListingContentNavigation.propTypes = {
  flags: PropTypes.shape({
    hasReviews: PropTypes.bool,
    hasSurroundings: PropTypes.bool
  }),
  className: PropTypes.string
};

export default ListingContentNavigation;
